// 設定画面（Issue #2）
//  - 控除項目マスター：全員共通の控除プルダウン項目を CRUD 編集
//  - 月スポット控除 履歴：対象月に各ドライバーへ入れた臨時控除を一覧化
//    （「入れた人しかわからない」を回避し、後から誰でも確認できるようにする）

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Ic } from "../components/Icons";
import { useStore } from "../store";
import { fetchPrevWork, restorePrevWork, type WorkMeta } from "../lib/workApi";
import { yen } from "../lib/normalize";
import { invoiceDiscountDivisor } from "../lib/calc/common";
import { isTargetNameUnmatched } from "../lib/calc/specialEarnings";
import { memberCsvUsage } from "../lib/calc/groupSum";
import type { DeductionMasterItem, SagawaBenkindCategory, ShidoRule, OfficeSumRule, GroupRule, Driver } from "../types";
import { resolveGroupInvoiceNo, isInvoiceNo, normalizeCompanyName } from "../lib/calc/groupInvoice";
import { normalizeName } from "../lib/normalize";
import { isRetiredDriver } from "../lib/driverStatus";

// 佐川 便種マッピングの区分ラベル（#32）
const SAGAWA_CAT_LABEL: Record<SagawaBenkindCategory, string> = {
  takkyu: "宅急便単価",
  nekopos: "ネコポス単価",
  other: "その他(280円)",
  ignore: "対象外",
};
const SAGAWA_CAT_ORDER: SagawaBenkindCategory[] = ["takkyu", "nekopos", "other", "ignore"];

// インボイス値引きの経過措置スケジュール（表示用）
const INVOICE_SCHEDULE: { from: string; label: string; divisor: number; pct: string }[] = [
  { from: "〜2026年9月", label: "仕入税額控除 80%", divisor: 5, pct: "消費税の 20%（1/5）" },
  { from: "2026年10月〜2029年9月", label: "仕入税額控除 50%", divisor: 2, pct: "消費税の 50%（1/2）" },
  { from: "2029年10月〜", label: "控除なし", divisor: 1, pct: "消費税の 全額（1/1）" },
];

// 共有データの巻き戻し（事故からの復旧）。
// 「過去月の再オープン」「人が選んだ上書き」「件数が減る保存」の直前に、
// （「全消去」ボタンは 2026-09-07 に廃止。翌月はその月のCSVを取り込めば切り替わる）
// サーバ側が1世代だけ内容を退避している。ここから戻せる。
function SharedWorkRecovery() {
  const { share, loadShared } = useStore();
  const [prev, setPrev] = useState<WorkMeta | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const reload = async () => {
    const r = await fetchPrevWork();
    setPrev(r.prev);
  };
  useEffect(() => {
    if (share.mode === "off") return;
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [share.mode]);

  if (share.mode === "off" || !prev) return null;

  const when = prev.savedAt ? new Date(prev.savedAt).toLocaleString("ja-JP") : "—";
  const monthLabel = /^\d{4}-\d{2}$/.test(prev.month)
    ? `${Number(prev.month.slice(0, 4))}年${Number(prev.month.slice(5, 7))}月`
    : prev.month || "—";

  const restore = async () => {
    if (
      !window.confirm(
        `共有データを1つ前の状態（${monthLabel}・${prev.count} 件・${when} 時点）に戻します。\n` +
          `いまの共有データは失われ、全員の画面がこの内容に切り替わります。\n\n続けますか？`
      )
    )
      return;
    setBusy(true);
    setMsg(null);
    const r = await restorePrevWork();
    if (r.ok) {
      await loadShared(); // 戻した内容を自分の画面にも反映する
      await reload();
      setMsg({ ok: true, text: "1つ前の状態に戻しました。他の方の画面にも数十秒で反映されます。" });
    } else {
      setMsg({ ok: false, text: r.error || "復元に失敗しました" });
    }
    setBusy(false);
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-head">
        <div className="card-title">共有データの巻き戻し</div>
        <span className="tiny muted">消してしまったときの復旧用</span>
      </div>
      <div className="card-pad col gap-12">
        <div className="tiny muted">
          「過去月の再オープン」「上書き保存」など<b>内容が大きく入れ替わる操作の直前</b>の状態を、
          1世代だけ保管しています。誤って消した・切り替えたときはここから戻せます
          （さらに前には戻せません）。
        </div>
        <div className="row gap-12" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <div className="col gap-4">
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              {monthLabel} ／ 明細 {prev.count} 件
            </div>
            <div className="tiny muted">
              {when} 時点（{prev.savedBy || "—"}）
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={restore} disabled={busy}>
            <Ic.history size={14} /> {busy ? "戻しています..." : "この状態に戻す"}
          </button>
        </div>
        {msg && (
          <div className={"toast " + (msg.ok ? "success" : "warn")} style={{ margin: 0 }}>
            {msg.ok ? <Ic.checkCircle size={14} /> : <Ic.alert size={14} />}
            <div style={{ flex: 1 }}>{msg.text}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SettingsScreen() {
  const { state, dispatch } = useStore();
  const master = state.deductionMaster;

  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editAmount, setEditAmount] = useState("");

  const nextId = () => `dm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  const handleAdd = () => {
    const n = name.trim();
    if (!n) {
      alert("控除項目名を入力してください");
      return;
    }
    if (master.some((m) => m.name === n)) {
      alert(`「${n}」は既に登録されています`);
      return;
    }
    const def = amount.trim() === "" ? null : Number(amount);
    if (def != null && (!Number.isFinite(def) || def < 0)) {
      alert("既定金額は0以上の数字で入力してください");
      return;
    }
    dispatch({ type: "ADD_DEDUCTION_ITEM", item: { id: nextId(), name: n, defaultAmount: def } });
    setName("");
    setAmount("");
  };

  const startEdit = (m: DeductionMasterItem) => {
    setEditingId(m.id);
    setEditName(m.name);
    setEditAmount(m.defaultAmount != null ? String(m.defaultAmount) : "");
  };

  const saveEdit = () => {
    const n = editName.trim();
    if (!n) {
      alert("控除項目名を入力してください");
      return;
    }
    if (master.some((m) => m.name === n && m.id !== editingId)) {
      alert(`「${n}」は既に登録されています`);
      return;
    }
    const def = editAmount.trim() === "" ? null : Number(editAmount);
    if (def != null && (!Number.isFinite(def) || def < 0)) {
      alert("既定金額は0以上の数字で入力してください");
      return;
    }
    dispatch({ type: "UPDATE_DEDUCTION_ITEM", item: { id: editingId!, name: n, defaultAmount: def } });
    setEditingId(null);
  };

  const handleDelete = (m: DeductionMasterItem) => {
    if (!window.confirm(`控除項目「${m.name}」をマスターから削除します。よろしいですか？\n（既に明細に入力済みの控除はそのまま残ります）`)) return;
    dispatch({ type: "DELETE_DEDUCTION_ITEM", id: m.id });
  };

  // ── 佐川 便種マッピング（#32） ──
  const benkindRules = state.sagawaBenkindRules;
  const [bCode, setBCode] = useState("");
  const [bName, setBName] = useState("");
  const [bCat, setBCat] = useState<SagawaBenkindCategory>("takkyu");

  const handleAddRule = () => {
    const code = bCode.trim();
    if (!code) {
      alert("便種コードを入力してください（例: 010）");
      return;
    }
    const exists = benkindRules.some((r) => r.code === code);
    dispatch({ type: exists ? "UPDATE_SAGAWA_RULE" : "ADD_SAGAWA_RULE", rule: { code, name: bName.trim() || undefined, category: bCat } });
    setBCode("");
    setBName("");
    setBCat("takkyu");
  };

  const handleChangeRuleCat = (code: string, name: string | undefined, category: SagawaBenkindCategory) => {
    dispatch({ type: "UPDATE_SAGAWA_RULE", rule: { code, name, category } });
  };

  const handleDeleteRule = (code: string) => {
    if (!window.confirm(`便種コード「${code}」のマッピングを削除します。\n削除すると、この便種を含むCSVは取込時に「未知の便種」として再びブロックされます。よろしいですか？`)) return;
    dispatch({ type: "DELETE_SAGAWA_RULE", code });
  };

  // 月スポット控除 履歴：対象月の各明細に入れた臨時控除を集める
  const spotHistory = useMemo(() => {
    const rows: { driverName: string; carrier: string; name: string; detail: string; amount: number }[] = [];
    for (const s of state.statements) {
      for (const e of s.extraDeductions || []) {
        rows.push({
          driverName: s.driverName,
          carrier: (s.carriers ?? [s.carrier]).map((c) => (c === "yamato" ? "ヤマト" : "佐川")).join("・"),
          name: e.name,
          detail: e.detail ?? "",
          amount: e.amount,
        });
      }
    }
    return rows;
  }, [state.statements]);

  const spotTotal = spotHistory.reduce((a, r) => a + r.amount, 0);

  return (
    <div className="content" style={{ maxWidth: 1100 }}>
      <div className="page-title-row">
        <div className="col gap-4">
          <div className="page-title">設定</div>
          <div className="page-sub muted">控除項目マスター・インボイス値引き・当月のスポット控除の履歴</div>
        </div>
      </div>

      <SharedWorkRecovery />

      {/* インボイス値引き（経過措置） */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head">
          <div className="card-title">インボイス値引き（経過措置）</div>
          <span className="tiny muted">未登録ドライバーの「値引き」控除の割合</span>
        </div>
        <div className="card-pad col gap-12">
          <div className="tiny muted">
            インボイス<b>未登録</b>のドライバーのみ、消費税額（<b>［課税売上 − 課税控除］× 10%</b>。
            ガソリン等の不課税は含めません）÷ 下表の分母を切り捨てて
            「<b>値引き</b>」として控除します（登録者は値引きなし）。割合は法令の経過措置に合わせて
            <b>対象月で自動的に切り替わり</b>ます（手動設定は不要）。
          </div>
          {(() => {
            const cur = invoiceDiscountDivisor(state.targetMonth);
            return (
              <div className="toast info" style={{ marginBottom: 0 }}>
                <Ic.info size={14} />
                <div className="tiny">
                  対象月 <b>{state.targetMonth}</b> は、値引き = <b>消費税 ÷ {cur}</b>
                  （{INVOICE_SCHEDULE.find((s) => s.divisor === cur)?.pct}）を適用中です。
                </div>
              </div>
            );
          })()}
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr><th>期間</th><th>仕入税額控除</th><th>値引き割合</th></tr>
              </thead>
              <tbody>
                {INVOICE_SCHEDULE.map((s) => {
                  const active = invoiceDiscountDivisor(state.targetMonth) === s.divisor;
                  return (
                    <tr key={s.from} style={active ? { background: "var(--blue-soft)" } : undefined}>
                      <td style={{ fontWeight: active ? 700 : 400 }}>{s.from}{active && <span className="chip blue" style={{ marginLeft: 8 }}>適用中</span>}</td>
                      <td className="muted">{s.label}</td>
                      <td className="num">{s.pct}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 控除項目マスター */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">控除項目マスター</div>
          <span className="tiny muted">{master.length} 項目 ／ 明細画面の「臨時控除」プルダウンに反映されます</span>
        </div>
        <div className="card-pad col gap-12">
          <div className="tiny muted">
            ガソリン代・ETC代など、よく使う控除項目をここで管理します。<b>全員共通</b>のプルダウンになります。
            既定金額を入れておくと、明細で選んだときに自動入力されます（金額は明細ごとに上書き可）。
          </div>

          {/* 追加フォーム */}
          <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="input"
              placeholder="控除項目名（例: 制服代）"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ flex: "1 1 220px", minWidth: 180 }}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <input
              className="input"
              type="number"
              min={0}
              step={1}
              placeholder="既定金額（任意・円）"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ width: 180, textAlign: "right" }}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <button className="btn primary" onClick={handleAdd}>
              <Ic.plus size={14} /> 追加
            </button>
          </div>

          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>控除項目名</th>
                  <th className="num">既定金額</th>
                  <th style={{ width: 140 }}></th>
                </tr>
              </thead>
              <tbody>
                {master.map((m) =>
                  editingId === m.id ? (
                    <tr key={m.id}>
                      <td>
                        <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                      </td>
                      <td className="num">
                        <input
                          className="input"
                          type="number"
                          min={0}
                          step={1}
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          style={{ width: 140, textAlign: "right" }}
                          placeholder="（任意）"
                        />
                      </td>
                      <td>
                        <div className="row gap-4">
                          <button className="btn sm primary" onClick={saveEdit}>
                            <Ic.check size={13} /> 保存
                          </button>
                          <button className="btn sm ghost" onClick={() => setEditingId(null)}>
                            取消
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={m.id}>
                      <td>{m.name}</td>
                      <td className="num">{m.defaultAmount != null ? `${yen(m.defaultAmount)} 円` : <span className="muted">—</span>}</td>
                      <td>
                        <div className="row gap-4">
                          <button className="btn sm ghost" title="編集" onClick={() => startEdit(m)}>
                            <Ic.edit size={13} />
                          </button>
                          <button className="btn sm ghost" title="削除" style={{ color: "#e64646" }} onClick={() => handleDelete(m)}>
                            <Ic.trash size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
                {master.length === 0 && (
                  <tr>
                    <td colSpan={3} className="muted" style={{ textAlign: "center", padding: 24 }}>
                      控除項目がありません。上のフォームから追加してください。
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 佐川 便種マッピング（#32） */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head">
          <div className="card-title">佐川 便種マッピング</div>
          <span className="tiny muted">{benkindRules.length} 件 ／ 佐川CSVの便種コードをどの単価で計上するか</span>
        </div>
        <div className="card-pad col gap-12">
          <div className="tiny muted">
            佐川CSVに<b>新しい便種</b>（例：ジャスト便）が出てくると、計上漏れ防止のため取込が一旦ブロックされます。
            ここで <b>便種コード</b> を<b>計上区分</b>に割り当てると、その便種が取り込めるようになります。
            便種コードは、取込画面のブロック表示（未知の便種の一覧）に出る番号です。
          </div>

          {/* 追加フォーム */}
          <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="input"
              placeholder="便種コード（例: 010）"
              value={bCode}
              onChange={(e) => setBCode(e.target.value)}
              style={{ width: 160 }}
              onKeyDown={(e) => e.key === "Enter" && handleAddRule()}
            />
            <input
              className="input"
              placeholder="便種名（任意・例: ジャスト便）"
              value={bName}
              onChange={(e) => setBName(e.target.value)}
              style={{ flex: "1 1 200px", minWidth: 160 }}
              onKeyDown={(e) => e.key === "Enter" && handleAddRule()}
            />
            <select className="input" value={bCat} onChange={(e) => setBCat(e.target.value as SagawaBenkindCategory)} style={{ width: 160 }}>
              {SAGAWA_CAT_ORDER.map((c) => (
                <option key={c} value={c}>{SAGAWA_CAT_LABEL[c]}</option>
              ))}
            </select>
            <button className="btn primary" onClick={handleAddRule}>
              <Ic.plus size={14} /> 追加
            </button>
          </div>

          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>便種コード</th>
                  <th>便種名</th>
                  <th style={{ width: 180 }}>計上区分</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {benkindRules.map((r) => (
                  <tr key={r.code}>
                    <td style={{ fontWeight: 700 }} className="num">{r.code}</td>
                    <td>{r.name || <span className="muted">—</span>}</td>
                    <td>
                      <select
                        className="input"
                        value={r.category}
                        onChange={(e) => handleChangeRuleCat(r.code, r.name, e.target.value as SagawaBenkindCategory)}
                        style={{ width: 160 }}
                      >
                        {SAGAWA_CAT_ORDER.map((c) => (
                          <option key={c} value={c}>{SAGAWA_CAT_LABEL[c]}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button className="btn sm ghost" title="削除" style={{ color: "#e64646" }} onClick={() => handleDeleteRule(r.code)}>
                        <Ic.trash size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
                {benkindRules.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted" style={{ textAlign: "center", padding: 24 }}>
                      便種マッピングがありません。上のフォームから追加してください。
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 指導料設定（本城さん 6/30 確定情報） */}
      <ShidoSettingsCard />

      {/* 営業所コード集計（勝本様） */}
      <OfficeSumSettingsCard />

      {/* グループ集計（AT-STYLE様 等。docs/33） */}
      <GroupSettingsCard />

      {/* 月スポット控除 履歴 */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head">
          <div className="card-title">スポット控除の履歴（{state.targetMonth}）</div>
          <span className="tiny muted">当月、各ドライバーの明細に入力された臨時控除の一覧</span>
        </div>
        <div className="tbl-scroll" style={{ maxHeight: 360 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>ドライバー</th>
                <th>区分</th>
                <th>控除項目</th>
                <th>備考</th>
                <th className="num">金額</th>
              </tr>
            </thead>
            <tbody>
              {spotHistory.map((r, i) => (
                <tr key={i}>
                  <td>{r.driverName}</td>
                  <td className="tiny muted">{r.carrier}</td>
                  <td>{r.name}</td>
                  <td className="tiny muted">{r.detail}</td>
                  <td className="num"><b>△{yen(r.amount)}</b></td>
                </tr>
              ))}
              {spotHistory.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ textAlign: "center", padding: 24 }}>
                    当月のスポット控除はまだありません。明細画面の「臨時控除」から入力すると、ここに集約されます。
                  </td>
                </tr>
              )}
            </tbody>
            {spotHistory.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4} className="num"><b>合計（{spotHistory.length} 件）</b></td>
                  <td className="num"><b>△{yen(spotTotal)}</b></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

// ===== 指導料設定（本城さん 6/30 確定情報。被指導者の月間個数×単価を指導者の収入として自動計上） =====

const newId = (p: string) => `${p}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

// 未保存の変更があることを、カードの先頭で目立たせる帯。
// これらの設定は「変更を保存（再計算）」を押すまで支払通知書に反映されない。
// 画面上は並び順や単価がすぐ変わって見えるため、保存が要ることに気づけない
// （本城さん 2026-08-07：並べ替えたのに通知書へ反映されない、というご指摘）。
function UnsavedSettingBar({ dirty, onSave, what }: { dirty: boolean; onSave: () => void; what: string }) {
  if (!dirty) return null;
  return (
    <div className="toast warn" style={{ margin: "12px 16px 0" }}>
      <Ic.alert size={14} />
      <div style={{ flex: 1 }}>
        {what}を変更しました。<b>「変更を保存（再計算）」</b>を押すと支払通知書に反映されます。
      </div>
      <button className="btn sm primary" onClick={onSave}>
        <Ic.check size={13} /> 変更を保存（再計算）
      </button>
    </div>
  );
}

// 数値入力（空=null、小数2桁まで）。不正入力は前の値を保持
function parseNum(v: string): number | null | undefined {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function ShidoSettingsCard() {
  const { state, dispatch } = useStore();
  const [rules, setRules] = useState<ShidoRule[]>(() => JSON.parse(JSON.stringify(state.shidoRules)) as ShidoRule[]);
  const dirty = JSON.stringify(rules) !== JSON.stringify(state.shidoRules);
  const yRowsAll = state.imports.yamato?.yamatoRows ?? [];
  const sRowsAll = state.imports.sagawa?.sagawaRows ?? [];

  const patchRule = (ri: number, patch: Partial<ShidoRule>) =>
    setRules((rs) => rs.map((r, i) => (i === ri ? { ...r, ...patch } : r)));
  const patchTarget = (ri: number, ti: string, patch: Partial<ShidoRule["targets"][number]>) =>
    setRules((rs) => rs.map((r, i) => (i === ri ? { ...r, targets: r.targets.map((t) => (t.id === ti ? { ...t, ...patch } : t)) } : r)));

  const save = () => {
    for (const r of rules) {
      if (!r.instructorName.trim()) { alert("指導者名が空の行があります"); return; }
      if (r.targets.some((t) => !t.targetName.trim() && (t.unitTakkyu != null || t.unitNekopos != null))) {
        alert(`「${r.instructorName}」に被指導者名が空の行があります`); return;
      }
    }
    dispatch({ type: "SET_SHIDO_RULES", rules });
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <div className="card-title">指導料設定</div>
        <span className="tiny muted">{rules.length} 名 ／ 被指導者の月間個数 × 単価（税抜）を指導者の収入として自動計上</span>
      </div>
      <UnsavedSettingBar dirty={dirty} onSave={save} what="指導料設定" />
      <div className="card-pad col gap-12">
        <div className="tiny muted">
          CSVを取り込むと、<b>被指導者</b>（CSV上の氏名で突合。マスター登録は不要）の宅急便・ネコポス個数を集計し、
          指導者の通知書に「指導料」行を自動で作ります。金額は行ごとに円未満切り捨て・課税10%です。
          設定がある指導者には、旧来の手入力式の指導料枠は表示されません。
        </div>
        {rules.map((r, ri) => (
          <div key={ri} className="col gap-8" style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}>
            <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
              <span className="tiny muted" style={{ fontWeight: 700 }}>指導者</span>
              <input className="input" value={r.instructorName} onChange={(e) => patchRule(ri, { instructorName: e.target.value })} placeholder="マスターの氏名（例: 豊島 竜太郎）" style={{ width: 200 }} />
              <span className="tiny muted" style={{ fontWeight: 700 }}>毎月固定指導料</span>
              <input
                className="input" type="number" min={0} step={1} placeholder="なし"
                value={r.fixedAmount ?? ""}
                onChange={(e) => { const n = parseNum(e.target.value); if (n !== undefined) patchRule(ri, { fixedAmount: n }); }}
                style={{ width: 130, textAlign: "right" }}
              />
              <span className="muted tiny">円</span>
              <div style={{ flex: 1 }} />
              <button className="btn sm ghost" style={{ color: "#e64646" }} onClick={() => { if (window.confirm(`「${r.instructorName}」の指導料設定を削除しますか？`)) setRules((rs) => rs.filter((_, i) => i !== ri)); }}>
                <Ic.trash size={13} /> 指導者を削除
              </button>
            </div>
            <table className="tbl">
              <thead>
                <tr><th>被指導者（CSV上の氏名）</th><th className="num" style={{ width: 140 }}>宅急便 円/個</th><th className="num" style={{ width: 140 }}>ネコポス 円/個</th><th style={{ width: 50 }}></th></tr>
              </thead>
              <tbody>
                {r.targets.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <input className="input" value={t.targetName} onChange={(e) => patchTarget(ri, t.id, { targetName: e.target.value })} placeholder="例: 兼光勇次" />
                      {t.targetName.trim() && isTargetNameUnmatched(t.targetName, yRowsAll, sRowsAll) && (
                        <div className="tiny" style={{ color: "var(--amber)", marginTop: 3 }}>
                          ⚠ 取込済みCSVに同名の担当者が見つかりません。CSV上の表記と完全一致が必要です
                        </div>
                      )}
                    </td>
                    <td className="num">
                      <input className="input" type="number" min={0} step={0.01} value={t.unitTakkyu ?? ""} placeholder="—"
                        onChange={(e) => { const n = parseNum(e.target.value); if (n !== undefined) patchTarget(ri, t.id, { unitTakkyu: n }); }}
                        style={{ width: 110, textAlign: "right" }} />
                    </td>
                    <td className="num">
                      <input className="input" type="number" min={0} step={0.01} value={t.unitNekopos ?? ""} placeholder="—"
                        onChange={(e) => { const n = parseNum(e.target.value); if (n !== undefined) patchTarget(ri, t.id, { unitNekopos: n }); }}
                        style={{ width: 110, textAlign: "right" }} />
                    </td>
                    <td>
                      <button className="btn sm ghost" style={{ color: "#e64646" }} title="この被指導者を削除"
                        onClick={() => patchRule(ri, { targets: r.targets.filter((x) => x.id !== t.id) })}>
                        <Ic.trash size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div>
              <button className="btn sm" onClick={() => patchRule(ri, { targets: [...r.targets, { id: newId("st"), targetName: "", unitTakkyu: null, unitNekopos: null }] })}>
                <Ic.plus size={13} /> 被指導者を追加
              </button>
            </div>
          </div>
        ))}
        <div className="row gap-8">
          <button className="btn" onClick={() => setRules((rs) => [...rs, { instructorName: "", fixedAmount: null, targets: [] }])}>
            <Ic.plus size={13} /> 指導者を追加
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn primary" onClick={save} disabled={!dirty}><Ic.check size={14} /> 変更を保存（再計算）</button>
        </div>
      </div>
    </div>
  );
}

// ===== 営業所コード集計（勝本様：ヤマトの指定営業所の個数合計×単価を計上） =====

function OfficeSumSettingsCard() {
  const { state, dispatch } = useStore();
  const [rules, setRules] = useState<OfficeSumRule[]>(() => JSON.parse(JSON.stringify(state.officeSumRules)) as OfficeSumRule[]);
  const dirty = JSON.stringify(rules) !== JSON.stringify(state.officeSumRules);

  const patch = (id: string, p: Partial<OfficeSumRule>) =>
    setRules((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));

  const save = () => {
    for (const r of rules) {
      if (!r.driverName.trim() || !r.officeCode.trim()) { alert("計上先ドライバー名と営業所コードは必須です"); return; }
    }
    dispatch({ type: "SET_OFFICE_SUM_RULES", rules });
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <div className="card-title">営業所コード集計</div>
        <span className="tiny muted">{rules.length} 件 ／ ヤマトCSVの指定営業所の宅急便・ネコポス個数合計を計上（勝本様等）</span>
      </div>
      <UnsavedSettingBar dirty={dirty} onSave={save} what="営業所コード集計" />
      <div className="card-pad col gap-12">
        <div className="tiny muted">
          ヤマトCSVの「営業所コード」列が一致する<b>全ドライバーの個数合計</b>を、指定した方の通知書に売上として計上します
          （本人がCSVに登場する月は、本人の配達分と合算されます）。
        </div>
        <div className="tbl-scroll">
          <table className="tbl">
            <thead>
              <tr><th>計上先（マスターの氏名）</th><th style={{ width: 140 }}>営業所コード</th><th className="num" style={{ width: 130 }}>宅急便 円/個</th><th className="num" style={{ width: 130 }}>ネコポス 円/個</th><th style={{ width: 50 }}></th></tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id}>
                  <td><input className="input" value={r.driverName} onChange={(e) => patch(r.id, { driverName: e.target.value })} placeholder="例: 勝本 和恵" /></td>
                  <td><input className="input" value={r.officeCode} onChange={(e) => patch(r.id, { officeCode: e.target.value })} placeholder="例: 73049" style={{ fontFamily: "monospace" }} /></td>
                  <td className="num">
                    <input className="input" type="number" min={0} step={0.01} value={r.unitTakkyu ?? ""} placeholder="—"
                      onChange={(e) => { const n = parseNum(e.target.value); if (n !== undefined) patch(r.id, { unitTakkyu: n }); }}
                      style={{ width: 100, textAlign: "right" }} />
                  </td>
                  <td className="num">
                    <input className="input" type="number" min={0} step={0.01} value={r.unitNekopos ?? ""} placeholder="—"
                      onChange={(e) => { const n = parseNum(e.target.value); if (n !== undefined) patch(r.id, { unitNekopos: n }); }}
                      style={{ width: 100, textAlign: "right" }} />
                  </td>
                  <td>
                    <button className="btn sm ghost" style={{ color: "#e64646" }} title="削除"
                      onClick={() => { if (window.confirm(`「${r.driverName}」の営業所集計を削除しますか？`)) setRules((rs) => rs.filter((x) => x.id !== r.id)); }}>
                      <Ic.trash size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 16 }}>営業所コード集計はありません</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="row gap-8">
          <button className="btn" onClick={() => setRules((rs) => [...rs, { id: newId("os"), driverName: "", officeCode: "", unitTakkyu: null, unitNekopos: null }])}>
            <Ic.plus size={13} /> 集計ルールを追加
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn primary" onClick={save} disabled={!dirty}><Ic.check size={14} /> 変更を保存（再計算）</button>
        </div>
      </div>
    </div>
  );
}

// ===== グループ集計（AT-STYLE様 等。docs/33。7/9 本城さん確定情報） =====
// グループ所属メンバーのCSV個数をグループ専用単価で集計し、グループ宛の通知書1枚を自動生成する。
// 単価は「ヤマトの周南・光」「ヤマトのそれ以外」「佐川」「佐川周南の対面以外」の4パターン。

function GroupSettingsCard() {
  const { state, dispatch } = useStore();
  const [rules, setRules] = useState<GroupRule[]>(() => JSON.parse(JSON.stringify(state.groupRules)) as GroupRule[]);
  const [memberInput, setMemberInput] = useState<Record<number, string>>({});
  const dirty = JSON.stringify(rules) !== JSON.stringify(state.groupRules);

  // この画面は編集用のコピーを持つ。開いている間に store 側が別経路で変わることがある
  // （入社フォームの AT-STYLE 追加・他の端末からの共有同期）。そのまま保存すると
  // 古いコピーで上書きし、追加されたメンバーが静かに消える（＝グループ通知書の金額が減る）ため、
  //   - この画面に未保存の編集が無ければ、黙って最新を取り込む
  //   - 未保存の編集があれば取り込まず、下のバナーで知らせて選ばせる
  const storeJson = JSON.stringify(state.groupRules);
  const lastStoreJson = useRef(storeJson);
  const [outdated, setOutdated] = useState(false);
  useEffect(() => {
    if (lastStoreJson.current === storeJson) return;
    const hadLocalEdit = JSON.stringify(rules) !== lastStoreJson.current;
    if (hadLocalEdit) {
      setOutdated(true);
    } else {
      setRules(JSON.parse(storeJson) as GroupRule[]);
      setOutdated(false);
    }
    lastStoreJson.current = storeJson;
    // rules は「取り込む前の状態」を見たいので依存に入れない（storeJson の変化だけで判定する）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeJson]);
  const yRowsAll = state.imports.yamato?.yamatoRows ?? [];
  const sRowsAll = state.imports.sagawa?.sagawaRows ?? [];

  const patchRule = (ri: number, patch: Partial<GroupRule>) =>
    setRules((rs) => rs.map((r, i) => (i === ri ? { ...r, ...patch } : r)));

  const addMember = (ri: number) => {
    const n = (memberInput[ri] || "").trim();
    if (!n) return;
    setRules((rs) => rs.map((r, i) => (i === ri ? { ...r, memberNames: [...r.memberNames, n] } : r)));
    setMemberInput((m) => ({ ...m, [ri]: "" }));
  };
  // 削除・並べ替えは「位置」で行う（同姓同名が登録されても取り違えないように）。
  //
  // 🔴 削除は金額に直結する（本城さん・徳永さん 2026-08-26 の突合で判明。docs/36 §8）。
  //    グループ集計は在籍/退職を見ないため、契約終了日を含む月はまだ実績があり、
  //    メンバーに残っていればその月は正しく計上される。逆に早く外すと、その月の分が
  //    グループ宛通知書から消えて支払額が減る（＝退職と同時に外すのが危険）。
  //    実績がある人・当月まで契約がある人を外そうとしたときは、何が起きるかを出して確認する。
  const removeMemberAt = (ri: number, mi: number) => {
    const rule = rules[ri];
    const name = rule?.memberNames[mi];
    if (!name) return;
    const usage = memberCsvUsage(name, yRowsAll, sRowsAll);
    const driver = state.drivers.find((d) => d.nameNormalized === normalizeName(name));
    // 契約終了日が対象月の月初以降＝この月はまだ支払対象（退職済みでも当月分は出る）
    const monthFirst = `${state.targetMonth}-01`;
    const endsThisMonthOrLater = !!driver?.contractTo && driver.contractTo >= monthFirst;

    const lines: string[] = [`「${name}」さんを ${rule.groupName} のメンバーから外しますか？`];
    if (usage.found) {
      const detail = [
        usage.yamatoTakkyu ? `宅急便 ${usage.yamatoTakkyu.toLocaleString()}個` : "",
        usage.yamatoNekopos ? `ネコポス ${usage.yamatoNekopos.toLocaleString()}個` : "",
        usage.sagawaQty ? `佐川 ${usage.sagawaQty.toLocaleString()}個` : "",
      ].filter(Boolean).join(" / ");
      lines.push("", `⚠️ ${state.targetMonth} の取込CSVに実績があります（${detail}）。`, "いま外すと、この月のグループ宛通知書からこの方の行が消え、支払額が減ります。");
    } else if (endsThisMonthOrLater) {
      lines.push("", `⚠️ 契約終了日が ${driver?.contractTo} です。${state.targetMonth} はまだ支払対象の月です。`, "CSVを取り込むと実績が出る可能性があります。");
    }
    if (usage.found || endsThisMonthOrLater) {
      lines.push("", "退職された方は、その月の支払処理が終わってから外してください。");
    }
    if (!window.confirm(lines.join("\n"))) return;
    setRules((rs) => rs.map((r, i) => (i === ri ? { ...r, memberNames: r.memberNames.filter((_, k) => k !== mi) } : r)));
  };

  // メンバーの並び順 = グループ宛通知書の行の並び順（groupSum.ts は memberNames の順に行を作る）。
  // 本城さん要望 2026-08-07：AT-STYLE様の通知書を部門ごとにまとめたい。
  const moveMember = (ri: number, mi: number, dir: -1 | 1) =>
    setRules((rs) =>
      rs.map((r, i) => {
        if (i !== ri) return r;
        const to = mi + dir;
        if (to < 0 || to >= r.memberNames.length) return r;
        const next = [...r.memberNames];
        [next[mi], next[to]] = [next[to], next[mi]];
        return { ...r, memberNames: next };
      })
    );

  // 部門（マスタの支店）ごとにまとめる。同じ部門の中の順番は今の並びを保つ（安定ソート）。
  // マスタに無い人は判定できないので末尾へ送る。
  const sortMembersByBranch = (ri: number) =>
    setRules((rs) =>
      rs.map((r, i) => {
        if (i !== ri) return r;
        const branchOf = (name: string) =>
          state.drivers.find((d) => d.nameNormalized === normalizeName(name))?.branch ?? "";
        const withIdx = r.memberNames.map((n, k) => ({ n, k, b: branchOf(n) }));
        withIdx.sort((a, b) => {
          if (a.b === b.b) return a.k - b.k; // 同部門は元の順を維持
          if (!a.b) return 1; // マスタ未登録は末尾
          if (!b.b) return -1;
          return a.b.localeCompare(b.b, "ja");
        });
        return { ...r, memberNames: withIdx.map((x) => x.n) };
      })
    );

  const save = () => {
    for (const r of rules) {
      if (!r.groupName.trim()) { alert("グループ名が空の行があります"); return; }
      const inv = (r.invoiceNo || "").trim();
      if (inv && !isInvoiceNo(inv)) {
        alert(`「${r.groupName}」の登録番号は T+13桁 で入力してください（例: T1234567890123）`);
        return;
      }
    }
    if (
      outdated &&
      !window.confirm(
        "この画面を開いている間に、別の場所（入社フォーム／他の端末）でグループ設定が変わりました。この画面の内容で上書きすると、その変更（例: 追加されたメンバー）は失われます。上書きしますか？"
      )
    ) {
      return;
    }
    setOutdated(false);
    lastStoreJson.current = JSON.stringify(rules);
    dispatch({ type: "SET_GROUP_RULES", rules });
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <div className="card-title">グループ集計</div>
        <span className="tiny muted">{rules.length} グループ ／ メンバーのCSV個数をグループ専用単価で集計し、グループ宛の通知書1枚を自動生成</span>
      </div>
      <UnsavedSettingBar dirty={dirty} onSave={save} what="グループ集計（メンバーの並び順・単価）" />
      {outdated && (
        <div className="toast warn" style={{ margin: "12px 16px 0" }}>
          <Ic.alert size={14} />
          <div style={{ flex: 1 }}>
            この画面を開いている間に、別の場所（入社フォーム／他の端末）でグループ設定が変わりました。
            このまま保存すると、その変更が失われます。
          </div>
          <button
            className="btn sm"
            onClick={() => {
              if (!window.confirm("この画面の未保存の編集を破棄して、最新の設定を取り込みますか？")) return;
              setRules(JSON.parse(JSON.stringify(state.groupRules)) as GroupRule[]);
              lastStoreJson.current = JSON.stringify(state.groupRules);
              setOutdated(false);
            }}
          >
            最新を取り込む
          </button>
        </div>
      )}
      <div className="card-pad col gap-12">
        <div className="tiny muted">
          単価はメンバーの<b>マスタ所属営業所（branch）</b>で自動判定します（ヤマト＝周南・光なら①、それ以外は②／
          佐川＝周南のみ③＋④、それ以外は③のみ）。メンバーの個人通知書（マスタ単価）は通常どおり全員分作成され、
          グループ宛通知書はそれに追加で1枚作られます（個別除外はしません）。
        </div>
        {rules.map((r, ri) => (
          <div key={ri} className="col gap-8" style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}>
            <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
              <span className="tiny muted" style={{ fontWeight: 700 }}>グループ名（宛名）</span>
              <input className="input" value={r.groupName} onChange={(e) => patchRule(ri, { groupName: e.target.value })} placeholder="例: 株式会社AT-STYLE" style={{ width: 220 }} />
              <div style={{ flex: 1 }} />
              <button className="btn sm ghost" style={{ color: "#e64646" }} onClick={() => { if (window.confirm(`「${r.groupName}」のグループ集計設定を削除しますか？`)) setRules((rs) => rs.filter((_, i) => i !== ri)); }}>
                <Ic.trash size={13} /> グループを削除
              </button>
            </div>

            {/* グループ宛通知書の送付先（本城さん・徳永さん 2026-08-10）。
                グループ宛はマスタに行が無いためメールアドレスを持てず、承認メールが送れなかった。 */}
            <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
              <span className="tiny muted" style={{ fontWeight: 700 }}>通知書の送付先メール</span>
              <input
                className="input"
                type="email"
                value={r.notifyEmail || ""}
                onChange={(e) => patchRule(ri, { notifyEmail: e.target.value })}
                placeholder="例: 代表者様のメールアドレス"
                style={{ width: 280 }}
              />
              <span className="tiny muted">
                {r.notifyEmail?.trim()
                  ? "「承認用に発行」でこの宛先へ自動送信されます"
                  : "未設定：このグループ宛の通知書はメール送信されません（URLを手で配布）"}
              </span>
            </div>

            {/* 通知書に出す登録番号（本城さん要望 2026-08-26）。
                未入力なら、マスタで「所属」がグループ名と一致する行の登録番号を自動で使う。 */}
            <GroupInvoiceRow rule={r} drivers={state.drivers} onChange={(v) => patchRule(ri, { invoiceNo: v })} />

            {/* マスタ「AT-STYLE」列との突合（本城さん要望 2026-08-26: マスタを見れば所属が分かるように）。
                集計の正は下のメンバーリスト。マスタ側にだけ印がある方を取りこぼさないための注意表示。 */}
            <GroupMasterMismatch
              rule={r}
              drivers={state.drivers}
              onAdd={(name) => setRules((rs) => rs.map((x, i) => (i === ri ? { ...x, memberNames: [...x.memberNames, name] } : x)))}
            />

            <div className="row gap-16" style={{ flexWrap: "wrap" }}>
              <div className="col gap-4">
                <span className="tiny muted">① ヤマト（周南・光）宅急便/ネコポス 円/個</span>
                <div className="row gap-4">
                  <input className="input" type="number" min={0} step={0.01} value={r.yamatoShunanHikari.takkyu}
                    onChange={(e) => { const n = parseNum(e.target.value); if (n != null) patchRule(ri, { yamatoShunanHikari: { ...r.yamatoShunanHikari, takkyu: n } }); }}
                    style={{ width: 90, textAlign: "right" }} />
                  <span className="tiny muted">/</span>
                  <input className="input" type="number" min={0} step={0.01} value={r.yamatoShunanHikari.nekopos}
                    onChange={(e) => { const n = parseNum(e.target.value); if (n != null) patchRule(ri, { yamatoShunanHikari: { ...r.yamatoShunanHikari, nekopos: n } }); }}
                    style={{ width: 90, textAlign: "right" }} />
                </div>
              </div>
              <div className="col gap-4">
                <span className="tiny muted">② ヤマト（それ以外）宅急便/ネコポス 円/個</span>
                <div className="row gap-4">
                  <input className="input" type="number" min={0} step={0.01} value={r.yamatoOther.takkyu}
                    onChange={(e) => { const n = parseNum(e.target.value); if (n != null) patchRule(ri, { yamatoOther: { ...r.yamatoOther, takkyu: n } }); }}
                    style={{ width: 90, textAlign: "right" }} />
                  <span className="tiny muted">/</span>
                  <input className="input" type="number" min={0} step={0.01} value={r.yamatoOther.nekopos}
                    onChange={(e) => { const n = parseNum(e.target.value); if (n != null) patchRule(ri, { yamatoOther: { ...r.yamatoOther, nekopos: n } }); }}
                    style={{ width: 90, textAlign: "right" }} />
                </div>
              </div>
              <div className="col gap-4">
                <span className="tiny muted">③ 佐川急便 円/件</span>
                <input className="input" type="number" min={0} step={0.01} value={r.sagawaTakkyu}
                  onChange={(e) => { const n = parseNum(e.target.value); if (n != null) patchRule(ri, { sagawaTakkyu: n }); }}
                  style={{ width: 90, textAlign: "right" }} />
              </div>
              <div className="col gap-4">
                <span className="tiny muted">④ 佐川周南「対面以外」円/件</span>
                <input className="input" type="number" min={0} step={0.01} value={r.sagawaTaimenIgai}
                  onChange={(e) => { const n = parseNum(e.target.value); if (n != null) patchRule(ri, { sagawaTaimenIgai: n }); }}
                  style={{ width: 90, textAlign: "right" }} />
              </div>
            </div>

            <div className="col gap-6">
              <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                <span className="tiny muted" style={{ fontWeight: 700 }}>
                  メンバー（{r.memberNames.length}名・マスタの氏名で指定）
                </span>
                <span className="tiny muted">— この並び順が、そのまま通知書の行の並び順になります</span>
                <div style={{ flex: 1 }} />
                <button
                  className="btn sm"
                  onClick={() => sortMembersByBranch(ri)}
                  disabled={r.memberNames.length < 2}
                  title="マスタの部門（支店）ごとにまとめて並べ替えます。同じ部門の中の順番は変わりません"
                >
                  <Ic.list size={12} /> 部門ごとに並べ替え
                </button>
              </div>
              <div className="tbl-scroll" style={{ maxHeight: 340 }}>
                <table className="tbl">
                  <tbody>
                    {r.memberNames.map((name, mi) => {
                      const driver = state.drivers.find((d) => d.nameNormalized === normalizeName(name));
                      const csvUnmatched = isTargetNameUnmatched(name, yRowsAll, sRowsAll);
                      // 部門が変わる位置に区切りを出す（部門ごとにまとまっているかが一目で分かる）
                      const prevBranch =
                        mi > 0
                          ? state.drivers.find((d) => d.nameNormalized === normalizeName(r.memberNames[mi - 1]))?.branch ?? ""
                          : null;
                      const branch = driver?.branch ?? "";
                      const newBranch = prevBranch !== null && prevBranch !== branch;
                      return (
                        <tr key={`${name}-${mi}`} style={newBranch ? { borderTop: "2px solid var(--border-strong)" } : undefined}>
                          <td className="tiny muted num" style={{ width: 34 }}>{mi + 1}</td>
                          <td style={{ fontWeight: 700 }}>{name}</td>
                          <td className="tiny muted" style={{ width: 110 }}>{branch || "—"}</td>
                          <td className="tiny" style={{ color: "var(--amber)" }}>
                            {!driver && <div>⚠ マスタ未登録（ヤマト単価は判定不可）</div>}
                            {csvUnmatched && <div>⚠ 取込済みCSVに同名の担当者が見つかりません</div>}
                          </td>
                          <td style={{ width: 116, whiteSpace: "nowrap", textAlign: "right" }}>
                            <button
                              className="btn sm ghost"
                              title="1つ上へ（通知書で前に出ます）"
                              disabled={mi === 0}
                              onClick={() => moveMember(ri, mi, -1)}
                            >
                              <Ic.chevronU size={13} />
                            </button>
                            <button
                              className="btn sm ghost"
                              title="1つ下へ（通知書で後に出ます）"
                              disabled={mi === r.memberNames.length - 1}
                              onClick={() => moveMember(ri, mi, 1)}
                            >
                              <Ic.chevronD size={13} />
                            </button>
                            <button
                              className="btn sm ghost"
                              title="削除"
                              style={{ color: "#e64646" }}
                              onClick={() => removeMemberAt(ri, mi)}
                            >
                              <Ic.trash size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {r.memberNames.length === 0 && (
                      <tr><td className="tiny muted" style={{ padding: 10 }}>メンバーが未登録です</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="row gap-6">
                <input className="input" value={memberInput[ri] || ""} onChange={(e) => setMemberInput((m) => ({ ...m, [ri]: e.target.value }))}
                  placeholder="例: 山本光一" style={{ width: 200 }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMember(ri); } }} />
                <button className="btn sm" onClick={() => addMember(ri)}><Ic.plus size={13} /> メンバーを追加</button>
              </div>
            </div>
          </div>
        ))}
        <div className="row gap-8">
          <button
            className="btn"
            onClick={() =>
              setRules((rs) => [
                ...rs,
                {
                  id: newId("grp"),
                  groupName: "",
                  notifyEmail: "",
                  invoiceNo: "",
                  memberNames: [],
                  yamatoShunanHikari: { takkyu: 15, nekopos: 10 },
                  yamatoOther: { takkyu: 20, nekopos: 10 },
                  sagawaTakkyu: 15,
                  sagawaTaimenIgai: 5,
                },
              ])
            }
          >
            <Ic.plus size={13} /> グループを追加
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn primary" onClick={save} disabled={!dirty}><Ic.check size={14} /> 変更を保存（再計算）</button>
        </div>
      </div>
    </div>
  );
}

// グループ宛通知書の登録番号（本城さん要望 2026-08-26「AT-STYLE様の通知書にインボイス番号が表示されていない」）。
// 未入力なら、マスタで「所属」がグループ名と一致する行の登録番号を自動で使う（AT-STYLE様なら
// 代表・淺田様の行の ㈱AT-STYLE / T…）。自動で拾えた番号もここに表示して、何が出るかを見えるようにする。
function GroupInvoiceRow({
  rule,
  drivers,
  onChange,
}: {
  rule: GroupRule;
  drivers: Driver[];
  onChange: (v: string) => void;
}) {
  const resolved = resolveGroupInvoiceNo(rule, drivers);
  const typed = (rule.invoiceNo || "").trim();
  const invalid = typed !== "" && !isInvoiceNo(typed);
  return (
    <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
      <span className="tiny muted" style={{ fontWeight: 700 }}>通知書の登録番号</span>
      <input
        className="input"
        value={rule.invoiceNo || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={resolved.source === "master" ? `${resolved.invoiceNo}（マスタから自動）` : "T1234567890123"}
        style={{ width: 200 }}
      />
      <span className="tiny" style={{ color: invalid ? "var(--red)" : undefined }}>
        {invalid ? (
          "登録番号は T+13桁 で入力してください（保存できません）"
        ) : resolved.source === "rule" ? (
          <span className="muted">この番号を通知書の宛名の下に表示します</span>
        ) : resolved.source === "master" ? (
          <span className="muted">
            未入力のため、マスタの「{resolved.fromDriverName}」さんの行（所属＝{rule.groupName}）にある{" "}
            <b>{resolved.invoiceNo}</b> を表示します
          </span>
        ) : resolved.ambiguous ? (
          <span style={{ color: "var(--amber)" }}>
            マスタに所属＝{rule.groupName} の行が複数あり、登録番号が一致しません（
            {(resolved.candidates ?? []).map((c) => `${c.name}: ${c.invoiceNo}`).join(" ／ ")}
            ）。どれが会社の登録番号かを、この欄に入力してください（未入力の間は通知書に表示されません）
          </span>
        ) : (
          <span style={{ color: "var(--amber)" }}>
            未設定：このグループ宛の通知書に「登録番号」は表示されません（マスタにも該当する所属の行がありません）
          </span>
        )}
      </span>
    </div>
  );
}

// マスタ「AT-STYLE」列とメンバーリストの突合（本城さん要望 2026-08-26）。
// 集計の正はメンバーリスト（金額に直結するため、シートの印だけで自動的に集計対象を増やさない）。
// マスタ側にだけ印がある方／メンバーなのにマスタに印が無い方を、ここで気づけるようにする。
function GroupMasterMismatch({
  rule,
  drivers,
  onAdd,
}: {
  rule: GroupRule;
  drivers: Driver[];
  onAdd: (name: string) => void;
}) {
  // マスタの「AT-STYLE」列は AT-STYLE様専用の列なので、AT-STYLE のグループにだけ突合を出す
  const isAtStyle = normalizeCompanyName(rule.groupName).includes("AT-STYLE");
  const hasColumn = drivers.some((d) => d.atStyle);
  if (!isAtStyle || !hasColumn) return null;

  const memberSet = new Set(rule.memberNames.map((n) => normalizeName(n)));
  const onlyInMaster = drivers.filter(
    (d) => d.atStyle && !isRetiredDriver(d) && !memberSet.has(d.nameNormalized)
  );
  const onlyInRule = rule.memberNames.filter((n) => {
    const d = drivers.find((x) => x.nameNormalized === normalizeName(n));
    return d && !isRetiredDriver(d) && !d.atStyle;
  });
  if (onlyInMaster.length === 0 && onlyInRule.length === 0) return null;

  return (
    <div className="col gap-4 tiny" style={{ background: "var(--surface-2)", borderRadius: 8, padding: "8px 10px" }}>
      {onlyInMaster.map((d) => (
        <div key={d.id} className="row gap-8" style={{ alignItems: "center" }}>
          <span style={{ color: "var(--amber)" }}>
            ⚠ マスタでは <b>{d.name}</b> さんが {rule.groupName} 所属ですが、下のメンバーに入っていません（集計対象外）
          </span>
          <button className="btn sm" onClick={() => onAdd(d.name)}>
            <Ic.plus size={12} /> メンバーに追加
          </button>
        </div>
      ))}
      {onlyInRule.length > 0 && (
        <div className="muted">
          メンバーだがマスタの「AT-STYLE」列が空欄：{onlyInRule.join("、")}
          　※集計はこのメンバーリストが正です。シート側の記入をおすすめします
        </div>
      )}
    </div>
  );
}
